// Canvas-only rendering for the torsion documentation workflow.
//
// Two responsibilities:
//   1. captureFrameWithOverlay() — composite a single live snapshot
//      (camera + horizon + goniometer + numeric readout) into a canvas at
//      the camera's intrinsic resolution.
//   2. buildTorsionReport() — assemble the start/end captures into one
//      side-by-side documentation image with the Δ corrected angle.
//
// Both replicate the on-screen visuals using pure 2D canvas, so the resulting
// images are sharp regardless of device pixel ratio and reproducible offline.

import { deltaAngleDeg } from './quaternion';
import { buildReport } from './reportFrame';

export interface TorsionCapture {
  /** Composite image (camera frame + overlay), at the camera's native resolution. */
  image: HTMLCanvasElement;
  /**
   * Absolute screen-roll angle at the moment of capture, in degrees.
   * Used to compute Δ between captures invariantly — even if the user
   * re-zeros after capturing, the rotation that actually happened between
   * Start and End is preserved.
   */
  absoluteAngle: number;
  /**
   * The angle as it was DISPLAYED on screen at the capture moment.
   * Equals `absoluteAngle` if no zero reference was active, or
   * `deltaAngleDeg(absoluteAngle, zeroReference)` otherwise.
   * Matches the number burned into the captured image's overlay.
   */
  displayAngleAtCapture: number;
  /** Capture timestamp (ms since epoch). */
  timestamp: number;
}

const CYAN = '#7cc4ff';
const ORANGE = '#ff9f0a';

/* ════════════════ Frame capture (live + overlay) ════════════════ */

export function captureFrameWithOverlay(
  video: HTMLVideoElement,
  liveAngle: number,
  referenceAngle: number | null,
): HTMLCanvasElement {
  const w = video.videoWidth || 1280;
  const h = video.videoHeight || 720;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;

  // 1. Camera frame
  ctx.drawImage(video, 0, 0, w, h);

  // 2. Vignette to lift the HUD legibility on bright fluoroscopy areas
  const vignette = ctx.createRadialGradient(
    w / 2, h / 2, Math.min(w, h) * 0.2,
    w / 2, h / 2, Math.max(w, h) * 0.7,
  );
  vignette.addColorStop(0, 'rgba(0,0,0,0)');
  vignette.addColorStop(1, 'rgba(0,0,0,0.5)');
  ctx.fillStyle = vignette;
  ctx.fillRect(0, 0, w, h);

  // 3. Horizon — counter-rotated so it stays world-horizontal in the captured frame
  drawHorizon(ctx, w, h, liveAngle);

  // 4. Goniometer ring + numeric readout
  drawGoniometer(ctx, w, h, liveAngle, referenceAngle);

  return canvas;
}

function drawHorizon(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  angle: number,
) {
  ctx.save();
  ctx.translate(w / 2, h / 2);
  ctx.rotate((-angle * Math.PI) / 180);

  const len = w * 1.5;
  const g = ctx.createLinearGradient(-len / 2, 0, len / 2, 0);
  g.addColorStop(0, 'rgba(124,196,255,0)');
  g.addColorStop(0.25, 'rgba(124,196,255,0.7)');
  g.addColorStop(0.75, 'rgba(124,196,255,0.7)');
  g.addColorStop(1, 'rgba(124,196,255,0)');
  ctx.strokeStyle = g;
  ctx.lineWidth = Math.max(1.5, Math.min(w, h) * 0.0015);
  ctx.beginPath();
  ctx.moveTo(-len / 2, 0);
  ctx.lineTo(len / 2, 0);
  ctx.stroke();

  ctx.restore();
}

function drawGoniometer(
  ctx: CanvasRenderingContext2D,
  w: number,
  h: number,
  liveAngle: number,
  referenceAngle: number | null,
) {
  const cx = w / 2;
  const cy = h / 2;
  const radius = Math.min(w, h) * 0.22; // ring radius in image px
  const refSet = referenceAngle != null;
  const display = refSet ? deltaAngleDeg(liveAngle, referenceAngle!) : liveAngle;

  // Backing disc
  ctx.fillStyle = 'rgba(0,0,0,0.36)';
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,255,255,0.32)';
  ctx.lineWidth = Math.max(1, radius * 0.005);
  ctx.beginPath();
  ctx.arc(cx, cy, radius, 0, Math.PI * 2);
  ctx.stroke();

  // Ticks
  for (let d = 0; d < 360; d += 5) {
    const major = d % 30 === 0;
    const a = ((d - 90) * Math.PI) / 180;
    const inner = major ? radius - radius * 0.085 : radius - radius * 0.04;
    ctx.beginPath();
    ctx.moveTo(cx + Math.cos(a) * inner, cy + Math.sin(a) * inner);
    ctx.lineTo(cx + Math.cos(a) * radius, cy + Math.sin(a) * radius);
    ctx.strokeStyle = major
      ? 'rgba(255,255,255,0.95)'
      : 'rgba(255,255,255,0.45)';
    ctx.lineWidth = major ? radius * 0.012 : radius * 0.006;
    ctx.lineCap = 'round';
    ctx.stroke();
  }

  // Major tick labels (every 30°)
  ctx.fillStyle = 'rgba(255,255,255,0.6)';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  const fontPx = Math.round(radius * 0.085);
  ctx.font = `${fontPx}px -apple-system, system-ui, sans-serif`;
  for (let d = 0; d < 360; d += 30) {
    const a = ((d - 90) * Math.PI) / 180;
    const lr = radius - radius * 0.18;
    const lbl = d > 180 ? d - 360 : d;
    ctx.fillText(`${Math.abs(lbl)}`, cx + Math.cos(a) * lr, cy + Math.sin(a) * lr);
  }

  // Reference marker (when reference is set, sits at 0° on the dial because
  // the live pointer is shown as a delta from the reference).
  if (refSet) {
    drawDialMarker(ctx, cx, cy, radius - radius * 0.1, 0, ORANGE, false);
  }

  // Live pointer
  drawDialMarker(ctx, cx, cy, radius - radius * 0.1, display, CYAN, true);

  // Centre readout (signed degrees)
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${Math.round(radius * 0.45)}px -apple-system, system-ui, sans-serif`;
  ctx.fillText(fmtSigned(display) + '°', cx, cy + (refSet ? -radius * 0.04 : 0));

  if (refSet) {
    ctx.font = `${Math.round(radius * 0.11)}px -apple-system, system-ui, sans-serif`;
    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.fillText(
      `Δ from ${fmtSigned(referenceAngle!)}°`,
      cx,
      cy + radius * 0.32,
    );
  }
}

function drawDialMarker(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  angleDeg: number,
  colour: string,
  withSpoke: boolean,
) {
  const a = ((angleDeg - 90) * Math.PI) / 180;
  const x = cx + Math.cos(a) * r;
  const y = cy + Math.sin(a) * r;

  if (withSpoke) {
    ctx.strokeStyle = colour;
    ctx.lineWidth = r * 0.018;
    ctx.lineCap = 'round';
    ctx.globalAlpha = 0.7;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(x, y);
    ctx.stroke();
    ctx.globalAlpha = 1;
  }

  ctx.fillStyle = 'rgba(0,0,0,0.5)';
  ctx.beginPath();
  ctx.arc(x, y, r * 0.06, 0, Math.PI * 2);
  ctx.fill();

  ctx.fillStyle = colour;
  ctx.beginPath();
  ctx.arc(x, y, r * 0.05, 0, Math.PI * 2);
  ctx.fill();
}

/* ════════════════ Side-by-side documentation report ════════════════ */

export function buildTorsionReport(
  start: TorsionCapture,
  end: TorsionCapture,
): HTMLCanvasElement {
  const delta = deltaAngleDeg(end.absoluteAngle, start.absoluteAngle);
  const result = `Δ Torsion: ${fmtSigned(delta)}°`;

  return buildReport(
    {
      module: 'torsion',
      variant: 'Live goniometer · gravity-referenced',
      result,
      timestamp: start.timestamp,
    },
    (ctx, rect) => {
      const COL_GAP = 32;
      const colW = (rect.w - COL_GAP) / 2;
      const srcAR = start.image.height / start.image.width;
      const colImgH = colW * srcAR;
      const ANGLE_LABEL_H = 88;
      // 180 (was 132) — extra 48 px leaves room for the precision-disclosure
      // footnote at the bottom of the panel without overlapping the big Δ.
      const DELTA_PANEL_H = 180;
      const SECTION_GAP = 28;

      const colY = rect.y;
      drawColumn(ctx, rect.x, colY, colW, colImgH, 'START', start);
      drawColumn(
        ctx,
        rect.x + colW + COL_GAP,
        colY,
        colW,
        colImgH,
        'END',
        end,
      );

      // Big Δ panel below the two columns
      const deltaY = colY + colImgH + ANGLE_LABEL_H + SECTION_GAP;
      drawDeltaPanel(ctx, rect.x, deltaY, rect.w, DELTA_PANEL_H, delta);

      return colImgH + ANGLE_LABEL_H + SECTION_GAP + DELTA_PANEL_H;
    },
  );
}

function drawColumn(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  imgH: number,
  label: string,
  cap: TorsionCapture,
) {
  const accent = CYAN;
  // Image with rounded corners
  const radius = 16;
  ctx.save();
  roundedRectPath(ctx, x, y, w, imgH, radius);
  ctx.clip();
  ctx.drawImage(cap.image, x, y, w, imgH);
  ctx.restore();

  // Image border
  ctx.strokeStyle = 'rgba(255,255,255,0.12)';
  ctx.lineWidth = 1;
  roundedRectPath(ctx, x + 0.5, y + 0.5, w - 1, imgH - 1, radius);
  ctx.stroke();

  // Label badge — top-left of the image
  const badgeText = label;
  ctx.font = 'bold 18px -apple-system, system-ui, sans-serif';
  const tw = ctx.measureText(badgeText).width;
  const bx = x + 16;
  const by = y + 16;
  const bh = 36;
  const bw = tw + 28;
  ctx.fillStyle = 'rgba(0,0,0,0.7)';
  roundedRectPath(ctx, bx, by, bw, bh, 18);
  ctx.fill();
  ctx.strokeStyle = accent;
  ctx.lineWidth = 1.5;
  roundedRectPath(ctx, bx + 0.5, by + 0.5, bw - 1, bh - 1, 18);
  ctx.stroke();
  ctx.fillStyle = accent;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(badgeText, bx + 14, by + bh / 2);

  // Angle label below the image
  const labelY = y + imgH + 14;
  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.textAlign = 'left';
  ctx.font =
    '14px -apple-system, system-ui, sans-serif';
  ctx.fillText('Captured angle', x + 4, labelY + 8);

  ctx.fillStyle = '#ffffff';
  ctx.font =
    'bold 38px -apple-system, system-ui, "SF Pro Display", sans-serif';
  // Show the value the surgeon actually saw at capture time (matches the
  // burned-in overlay number on the image above).
  ctx.fillText(`${fmtSigned(cap.displayAngleAtCapture)}°`, x + 4, labelY + 40);

  // Time on the right
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.textAlign = 'right';
  ctx.font =
    '14px -apple-system, system-ui, sans-serif';
  ctx.fillText(formatClockTime(cap.timestamp), x + w - 4, labelY + 30);
}

function drawDeltaPanel(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  delta: number,
) {
  // A prominent orange-accented panel that visually pops as the report's
  // primary outcome.
  ctx.fillStyle = '#161618';
  roundedRectPath(ctx, x, y, w, h, 18);
  ctx.fill();
  ctx.strokeStyle = 'rgba(255,159,10,0.32)';
  ctx.lineWidth = 1.5;
  roundedRectPath(ctx, x + 0.75, y + 0.75, w - 1.5, h - 1.5, 18);
  ctx.stroke();

  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';

  ctx.fillStyle = 'rgba(255,255,255,0.55)';
  ctx.font = '600 14px -apple-system, system-ui, sans-serif';
  ctx.fillText('Δ TORSIONAL CORRECTION', x + w / 2, y + 28);

  ctx.fillStyle = '#ffffff';
  ctx.font =
    'bold 76px -apple-system, BlinkMacSystemFont, "SF Pro Display", system-ui, sans-serif';
  ctx.fillText(`${fmtSigned(delta)}°`, x + w / 2, y + h / 2 + 12);

  // Honesty footnote: the start, end, and Δ values are each independently
  // rounded to 0.1° for display. The Δ itself is computed from the FULL-
  // precision sensor readings, so naive `end − start` on the displayed
  // values may differ from the displayed Δ by up to 0.1°. This is more
  // accurate than rounding twice — and saying so up front avoids confusion
  // when an operator does the math in their head.
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.font = '400 16px -apple-system, system-ui, sans-serif';
  ctx.fillText(
    'Δ computed at full sensor precision · displayed values rounded to 0.1°',
    x + w / 2,
    y + h - 24,
  );
}

/* ════════════════ Small utilities ════════════════ */

function fmtSigned(deg: number): string {
  const sign = deg > 0 ? '+' : deg < 0 ? '−' : '';
  return `${sign}${Math.abs(deg).toFixed(1)}`;
}

function formatClockTime(ts: number): string {
  const d = new Date(ts);
  const pad = (n: number) => n.toString().padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function roundedRectPath(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
}
